import { useEffect, useRef, useState } from "react";
import { api, getApiError } from "@/lib/api";
import { formatLastSeen } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import TypingDots from "@/components/TypingDots";
import TranslateButton from "@/components/TranslateButton";

const TYPING_HEARTBEAT_MS = 3000;

export default function MemberMessages() {
  const [messages, setMessages] = useState([]);
  const [presence, setPresence] = useState({ last_seen: null, is_typing: false });
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const lastTypedRef = useRef(0);

  const refresh = async (silent = false) => {
    try {
      const r = await api.get("/messages");
      setMessages(r.data.messages || []);
      setPresence(r.data.presence || { last_seen: null, is_typing: false });
    } catch (e) { if (!silent) toast.error(getApiError(e)); }
  };

  useEffect(() => { refresh(); }, []);

  // Poll for new admin replies + presence every 5s while tab is visible.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) refresh(true); }, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await api.post("/messages", { body: text });
      setMessages((prev) => [...prev, r.data]);
      setBody("");
    } catch (e) { toast.error(getApiError(e)); }
    finally { setSending(false); }
  };

  const onChangeBody = (e) => {
    setBody(e.target.value);
    const now = Date.now();
    if (now - lastTypedRef.current > TYPING_HEARTBEAT_MS) {
      lastTypedRef.current = now;
      api.post("/messages/typing").catch(() => {});
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
  };

  const seen = formatLastSeen(presence.last_seen);

  return (
    <Card className="border-black/5" data-testid="member-messages-card">
      <CardHeader>
        <CardTitle className="font-serif text-2xl flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[color:var(--orange)]" />
          Message your trustee
        </CardTitle>
        <div className="text-xs text-[color:var(--text-secondary)] flex items-center gap-2" data-testid="member-presence">
          {presence.is_typing ? (
            <span className="flex items-center gap-1.5 text-[color:var(--bright-green)]" data-testid="member-presence-typing">
              Trustee is typing <TypingDots />
            </span>
          ) : seen ? (
            <span data-testid="member-presence-seen">Trustee last seen {seen}</span>
          ) : (
            <span>Direct, in-house messages to the Guild admin.</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          ref={scrollRef}
          className="max-h-80 overflow-y-auto space-y-3 pr-1"
          data-testid="member-messages-list"
        >
          {messages.length === 0 && (
            <p className="text-sm text-[color:var(--text-secondary)] text-center py-6" data-testid="member-messages-empty">
              No messages yet. Start the conversation below.
            </p>
          )}
          {messages.map((m) => {
            const mine = m.sender === "member";
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`member-msg-${m.message_id}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-[color:var(--fire-red)] text-white" : "bg-[color:var(--bg-secondary)] text-[color:var(--text-primary)]"}`}>
                  {!mine && (
                    <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 mb-1">
                      {m.admin_name || "Trustee"}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</p>
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[color:var(--text-secondary)]"}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                  <TranslateButton messageId={m.message_id} theme={mine ? "dark" : "light"} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={onChangeBody}
            onKeyDown={onKeyDown}
            placeholder="Type your message to the Guild trustee… (⌘/Ctrl + Enter to send)"
            rows={3}
            data-testid="member-msg-input"
          />
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="border-black/10 text-[10px]">In-house · Encrypted at rest</Badge>
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white"
              data-testid="member-msg-send-btn"
            >
              <Send className="h-4 w-4 mr-1.5" />{sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
